/**
 * Device-type-specific inspection schema. Defines the manufacturer-picker
 * shape, the damage-step rows, and the item-status-step rows for every
 * check-in device type.
 *
 * The wizard renders steps from this schema directly — adding a row for a
 * new device type only requires editing this file.
 *
 * The data-model contracts (LaptopInspection / DesktopInspection /
 * AllInOneInspection) group rows differently: laptop has separate damage +
 * itemStatus, desktop+AIO have a single `checks` group. The mappers at the
 * bottom translate between the UX-time draft (two groups, regardless of
 * device type) and the contract-time discriminated union.
 */

import type {
  AllInOneInspection,
  CheckInDeviceType,
  CheckInPhotoShot,
  CheckInInspectionAiFilled,
  CheckInPhoto,
  CheckInInspection,
  CheckInInspectionCheck,
  ConsoleInspection,
  DesktopInspection,
  HardDriveInspection,
  LaptopInspection,
  OtherDeviceInspection,
  PhoneInspection,
  TabletInspection,
  UsbFlashDriveInspection,
} from '@techgitter/contracts';

import {
  AIO_MFR_OPTIONS,
  CONSOLE_MFR_OPTIONS,
  DESKTOP_MFR_OPTIONS,
  HARD_DRIVE_MFR_OPTIONS,
  LAPTOP_MFR_SUGGESTIONS,
  PHONE_MFR_SUGGESTIONS,
  TABLET_MFR_SUGGESTIONS,
  USB_FLASH_DRIVE_MFR_OPTIONS,
} from '../data/inspectionManufacturers';

export interface InspectionCheckRow {
  key: string;
  label: string;
}

export interface PhotoShotDef {
  key: CheckInPhotoShot;
  label: string;
  /** Second line on the tile ("Closed"). */
  detail?: string;
}

const ACCESSORIES: PhotoShotDef = { key: 'accessories', label: 'Accessories', detail: 'cables, chargers, cases' };
const SERIAL_LABEL = 'Serial number label';

/** A sealed box is one photo: the side of the box carrying the serial label. */
export const SEALED_BOX_SERIAL_SHOT = {
  label: 'Box side with label',
  hint: 'The side of the box that shows the serial number — the only photo a sealed box needs.',
};

export interface InspectionSchemaEntry {
  deviceType: CheckInDeviceType;
  /** 'autocomplete' = free text with suggestions; 'dropdown' = fixed list. */
  manufacturerKind: 'autocomplete' | 'dropdown';
  /** Suggestions (autocomplete) or options (dropdown). */
  manufacturerOptions: string[];
  /** Returns true when serialNumber is optional for the given manufacturer. */
  serialOptional?: (manufacturer: string | null | undefined) => boolean;
  /** "No visible serial" — true for every type since the guided photo shots
   *  (2026-10-10); kept as a field because the serial-correction dialog reads it. */
  supportsSerialNotLegible: boolean;
  /** The serial tile, first in Photos: what to call it and where to look. */
  serialShot: { label: string; hint: string };
  /** The suggested condition shots after the serial label, in report order.
   *  Guided, never required. Every type ends with Areas of interest (not listed). */
  photoShots: PhotoShotDef[];
  /** Empty array = damage step skipped for this device type. */
  damageRows: InspectionCheckRow[];
  itemStatusRows: InspectionCheckRow[];
}

// Manufacturer lists moved to ../data/inspectionManufacturers.ts —
// re-exported here for backwards compatibility with anything that imports
// them through this module.
export {
  LAPTOP_MFR_SUGGESTIONS,
  DESKTOP_MFR_OPTIONS,
  AIO_MFR_OPTIONS,
  PHONE_MFR_SUGGESTIONS,
  TABLET_MFR_SUGGESTIONS,
  HARD_DRIVE_MFR_OPTIONS,
  USB_FLASH_DRIVE_MFR_OPTIONS,
  CONSOLE_MFR_OPTIONS,
};

export const INSPECTION_SCHEMA: Record<CheckInDeviceType, InspectionSchemaEntry> = {
  laptop: {
    deviceType: 'laptop',
    serialShot: { label: SERIAL_LABEL, hint: 'Usually on the underside.' },
    photoShots: [
      { key: 'front_open', label: 'Front-on', detail: 'Open' },
      { key: 'front_closed', label: 'Front-on', detail: 'Closed' },
      { key: 'lhs_closed', label: 'LHS', detail: 'Closed' },
      { key: 'rhs_closed', label: 'RHS', detail: 'Closed' },
      { key: 'back_closed', label: 'Back', detail: 'Closed' },
      { key: 'underside_closed', label: 'Underside', detail: 'Closed' },
      { key: 'top_closed', label: 'Top', detail: 'Closed' },
      ACCESSORIES,
    ],
    manufacturerKind: 'autocomplete',
    manufacturerOptions: LAPTOP_MFR_SUGGESTIONS,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'display', label: 'Display' },
      { key: 'displayBezel', label: 'Display bezel' },
      { key: 'displayChassis', label: 'Display chassis' },
      { key: 'hinges', label: 'Hinges' },
      { key: 'palmrest', label: 'Palmrest' },
      { key: 'bottomChassis', label: 'Bottom chassis' },
    ],
    itemStatusRows: [
      { key: 'allFeetPresent', label: 'All feet present' },
      { key: 'allScrewsPresent', label: 'All screws present' },
      { key: 'wobbles', label: 'Wobbles' },
      { key: 'usbItems', label: 'USB items' },
      { key: 'powerAdapter', label: 'Power adapter' },
    ],
  },
  desktop: {
    deviceType: 'desktop',
    serialShot: { label: SERIAL_LABEL, hint: 'Usually on the rear or a side panel.' },
    photoShots: [
      { key: 'front', label: 'Front' },
      { key: 'top', label: 'Top' },
      { key: 'rear', label: 'Rear' },
      { key: 'lhs', label: 'LHS' },
      { key: 'rhs', label: 'RHS' },
      ACCESSORIES,
    ],
    manufacturerKind: 'dropdown',
    manufacturerOptions: DESKTOP_MFR_OPTIONS,
    serialOptional: m => m === 'Custom',
    supportsSerialNotLegible: true,
    damageRows: [],
    itemStatusRows: [
      { key: 'allFeetPresent', label: 'All feet present' },
      { key: 'usbItems', label: 'USB items' },
    ],
  },
  all_in_one: {
    deviceType: 'all_in_one',
    serialShot: { label: SERIAL_LABEL, hint: 'Usually on the back or under the stand.' },
    photoShots: [
      { key: 'front', label: 'Front' },
      { key: 'rear', label: 'Rear' },
      ACCESSORIES,
    ],
    manufacturerKind: 'dropdown',
    manufacturerOptions: AIO_MFR_OPTIONS,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'screenDamage', label: 'Screen damage' },
    ],
    itemStatusRows: [
      { key: 'allFeetPresent', label: 'All feet present' },
      { key: 'usbItems', label: 'USB items' },
    ],
  },
  phone: {
    deviceType: 'phone',
    serialShot: { label: 'IMEI / serial', hint: "Printed on the SIM tray, the back, or the box. We don't power devices on at reception." },
    photoShots: [{ key: 'front', label: 'Front' }, { key: 'back', label: 'Back' }, ACCESSORIES],
    manufacturerKind: 'autocomplete',
    manufacturerOptions: PHONE_MFR_SUGGESTIONS,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'screenGlass', label: 'Screen / glass' },
      { key: 'rearHousing', label: 'Rear housing' },
      { key: 'frameCorners', label: 'Frame & corners' },
      { key: 'cameraLens', label: 'Camera lens' },
    ],
    itemStatusRows: [
      { key: 'buttons', label: 'Buttons (power/volume)' },
      { key: 'chargePort', label: 'Charge port' },
      { key: 'simTray', label: 'SIM tray present' },
      { key: 'accessories', label: 'Accessories present' },
    ],
  },
  tablet: {
    deviceType: 'tablet',
    serialShot: { label: SERIAL_LABEL, hint: "Usually printed small on the back. We don't power devices on at reception." },
    photoShots: [{ key: 'front', label: 'Front' }, { key: 'back', label: 'Back' }, ACCESSORIES],
    manufacturerKind: 'autocomplete',
    manufacturerOptions: TABLET_MFR_SUGGESTIONS,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'screenGlass', label: 'Screen / glass' },
      { key: 'rearHousing', label: 'Rear housing' },
      { key: 'frameCorners', label: 'Frame & corners' },
      { key: 'cameraLens', label: 'Camera lens' },
    ],
    itemStatusRows: [
      { key: 'buttons', label: 'Buttons' },
      { key: 'chargePort', label: 'Charge port' },
      { key: 'accessories', label: 'Accessories present' },
    ],
  },
  hard_drive: {
    deviceType: 'hard_drive',
    serialShot: { label: 'Side with label', hint: 'The label carries the serial — this one photo covers both.' },
    photoShots: [{ key: 'usb_port_side', label: 'Side with USB port' }, ACCESSORIES],
    manufacturerKind: 'dropdown',
    manufacturerOptions: HARD_DRIVE_MFR_OPTIONS,
    // Drive labels are frequently worn — serial is always optional.
    serialOptional: () => true,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'enclosure', label: 'Enclosure / casing' },
      { key: 'connector', label: 'Connector / interface pins' },
      { key: 'dropImpact', label: 'Signs of drop / impact' },
    ],
    itemStatusRows: [
      { key: 'cablePresent', label: 'Cable / power adapter present' },
      { key: 'caddyPresent', label: 'Caddy / enclosure present' },
    ],
  },
  usb_flash_drive: {
    deviceType: 'usb_flash_drive',
    serialShot: { label: SERIAL_LABEL, hint: 'Often printed on the casing or the connector; many sticks have none.' },
    photoShots: [
      { key: 'front', label: 'Front' },
      { key: 'back', label: 'Back' },
      { key: 'connector', label: 'Connector' },
      ACCESSORIES,
    ],
    manufacturerKind: 'dropdown',
    manufacturerOptions: USB_FLASH_DRIVE_MFR_OPTIONS,
    // Many USB sticks carry no serial — always optional.
    serialOptional: () => true,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'usbConnector', label: 'USB connector / plug' },
      { key: 'casing', label: 'Casing' },
    ],
    itemStatusRows: [
      { key: 'capPresent', label: 'Cap present' },
    ],
  },
  console: {
    deviceType: 'console',
    serialShot: { label: SERIAL_LABEL, hint: 'Usually on the underside or the rear.' },
    photoShots: [
      { key: 'front', label: 'Front' },
      { key: 'back', label: 'Back', detail: 'ports' },
      { key: 'top', label: 'Top' },
      { key: 'lhs', label: 'LHS' },
      { key: 'rhs', label: 'RHS' },
      ACCESSORIES,
    ],
    manufacturerKind: 'dropdown',
    manufacturerOptions: CONSOLE_MFR_OPTIONS,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'casing', label: 'Casing' },
      { key: 'ports', label: 'Ports (HDMI / USB)' },
      { key: 'discDrive', label: 'Disc drive / cartridge slot' },
    ],
    itemStatusRows: [
      { key: 'controllers', label: 'Controller(s) present' },
      { key: 'powerCable', label: 'Power cable present' },
      { key: 'hdmiCable', label: 'HDMI cable present' },
    ],
  },
  other: {
    deviceType: 'other',
    serialShot: { label: SERIAL_LABEL, hint: "Wherever the device's label is." },
    photoShots: [{ key: 'front', label: 'Front' }, { key: 'back', label: 'Back' }, ACCESSORIES],
    // Free text with no suggestions: by definition there is no list for it.
    manufacturerKind: 'autocomplete',
    manufacturerOptions: [],
    // No telling whether an unknown device carries a serial at all.
    serialOptional: () => true,
    supportsSerialNotLegible: true,
    damageRows: [
      { key: 'casing', label: 'Casing / body' },
      { key: 'screen', label: 'Screen (if any)' },
      { key: 'ports', label: 'Ports / connectors' },
    ],
    itemStatusRows: [
      { key: 'powerAdapter', label: 'Power adapter / charger present' },
      { key: 'accessories', label: 'Accessories present' },
    ],
  },
};

// ---------------------------------------------------------------------------
// UX-time draft <-> contract mapping
// ---------------------------------------------------------------------------

export interface InspectionDraftState {
  manufacturer: string | null;
  serialNumber: string | null;
  /** As typed; normalised on the way out (`normaliseModelNumber`). */
  modelNumber: string | null;
  /** Apple computers only; normalised on the way out (`normaliseEmcNumber`). */
  emcNumber: string | null;
  serialNotLegible: boolean;
  /** True when the device arrived sealed in its factory box — the wizard
   * skips the item-status step when this is set. */
  brandNewInBox: boolean;
  damageChecks: Record<string, CheckInInspectionCheck>;
  itemStatusChecks: Record<string, CheckInInspectionCheck>;
  otherNotes: string | null;
  /** Identity fields pre-filled from a photo, keyed to that photo's id. */
  aiFilled: CheckInInspectionAiFilled;
}

export function emptyDraft(): InspectionDraftState {
  return {
    manufacturer: null,
    serialNumber: null,
    modelNumber: null,
    emcNumber: null,
    serialNotLegible: false,
    brandNewInBox: false,
    damageChecks: {},
    itemStatusChecks: {},
    otherNotes: null,
    aiFilled: {},
  };
}

const EMPTY_CHECK: CheckInInspectionCheck = { status: null, notes: null };

function pickCheck(
  source: Record<string, CheckInInspectionCheck> | undefined,
  key: string,
): CheckInInspectionCheck {
  return source?.[key] ?? EMPTY_CHECK;
}

/** Hydrate the draft state from whatever the server returned (or empty). */
export function inspectionToDraft(
  deviceType: CheckInDeviceType,
  inspection: CheckInInspection | null | undefined,
): InspectionDraftState {
  const draft = inspectionToDraftByType(deviceType, inspection);
  // Every device type carries these the same way, so they are read once here
  // rather than in each branch below.
  return {
    ...draft,
    modelNumber: inspection?.modelNumber ?? null,
    emcNumber: inspection?.emcNumber ?? null,
    aiFilled: { ...(inspection?.aiFilled ?? {}) },
  };
}

function inspectionToDraftByType(
  deviceType: CheckInDeviceType,
  inspection: CheckInInspection | null | undefined,
): Omit<InspectionDraftState, 'aiFilled' | 'modelNumber' | 'emcNumber'> {
  if (!inspection) return emptyDraft();

  if (deviceType === 'laptop' && inspection.deviceType === 'laptop') {
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: { ...inspection.damageChecks } as unknown as Record<string, CheckInInspectionCheck>,
      itemStatusChecks: { ...inspection.itemStatusChecks } as unknown as Record<string, CheckInInspectionCheck>,
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'desktop' && inspection.deviceType === 'desktop') {
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: !!inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: {},
      itemStatusChecks: { ...inspection.checks } as unknown as Record<string, CheckInInspectionCheck>,
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'all_in_one' && inspection.deviceType === 'all_in_one') {
    const checks = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: !!inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: { screenDamage: checks.screenDamage },
      itemStatusChecks: {
        allFeetPresent: checks.allFeetPresent,
        usbItems: checks.usbItems,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'phone' && inspection.deviceType === 'phone') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: {
        screenGlass: c.screenGlass,
        rearHousing: c.rearHousing,
        frameCorners: c.frameCorners,
        cameraLens: c.cameraLens,
      },
      itemStatusChecks: {
        buttons: c.buttons,
        chargePort: c.chargePort,
        simTray: c.simTray,
        accessories: c.accessories,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'tablet' && inspection.deviceType === 'tablet') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: {
        screenGlass: c.screenGlass,
        rearHousing: c.rearHousing,
        frameCorners: c.frameCorners,
        cameraLens: c.cameraLens,
      },
      itemStatusChecks: {
        buttons: c.buttons,
        chargePort: c.chargePort,
        accessories: c.accessories,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'hard_drive' && inspection.deviceType === 'hard_drive') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: !!inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: {
        enclosure: c.enclosure,
        connector: c.connector,
        dropImpact: c.dropImpact,
      },
      itemStatusChecks: {
        cablePresent: c.cablePresent,
        caddyPresent: c.caddyPresent,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'usb_flash_drive' && inspection.deviceType === 'usb_flash_drive') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: !!inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: {
        usbConnector: c.usbConnector,
        casing: c.casing,
      },
      itemStatusChecks: {
        capPresent: c.capPresent,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'console' && inspection.deviceType === 'console') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: { casing: c.casing, ports: c.ports, discDrive: c.discDrive },
      itemStatusChecks: {
        controllers: c.controllers,
        powerCable: c.powerCable,
        hdmiCable: c.hdmiCable,
      },
      otherNotes: inspection.otherNotes,
    };
  }
  if (deviceType === 'other' && inspection.deviceType === 'other') {
    const c = inspection.checks;
    return {
      manufacturer: inspection.manufacturer,
      serialNumber: inspection.serialNumber,
      serialNotLegible: !!inspection.serialNotLegible,
      brandNewInBox: !!inspection.brandNewInBox,
      damageChecks: { casing: c.casing, screen: c.screen, ports: c.ports },
      itemStatusChecks: { powerAdapter: c.powerAdapter, accessories: c.accessories },
      otherNotes: inspection.otherNotes,
    };
  }
  return emptyDraft();
}

/** Build the discriminated-union inspection object for PATCH. */
export function draftToInspection(
  deviceType: CheckInDeviceType,
  draft: InspectionDraftState,
): CheckInInspection {
  const inspection = {
    ...draftToInspectionByType(deviceType, draft),
    modelNumber: normaliseModelNumber(draft.modelNumber),
    emcNumber: normaliseEmcNumber(draft.emcNumber),
  };
  const aiFilled = Object.fromEntries(
    Object.entries(draft.aiFilled ?? {}).filter(([, photoId]) => !!photoId),
  ) as CheckInInspectionAiFilled;
  return Object.keys(aiFilled).length ? { ...inspection, aiFilled } : inspection;
}

function draftToInspectionByType(
  deviceType: CheckInDeviceType,
  draft: InspectionDraftState,
): CheckInInspection {
  if (deviceType === 'laptop') {
    const laptop: LaptopInspection = {
      deviceType: 'laptop',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      damageChecks: {
        display: pickCheck(draft.damageChecks, 'display'),
        displayBezel: pickCheck(draft.damageChecks, 'displayBezel'),
        displayChassis: pickCheck(draft.damageChecks, 'displayChassis'),
        hinges: pickCheck(draft.damageChecks, 'hinges'),
        palmrest: pickCheck(draft.damageChecks, 'palmrest'),
        bottomChassis: pickCheck(draft.damageChecks, 'bottomChassis'),
      },
      itemStatusChecks: {
        allFeetPresent: pickCheck(draft.itemStatusChecks, 'allFeetPresent'),
        allScrewsPresent: pickCheck(draft.itemStatusChecks, 'allScrewsPresent'),
        wobbles: pickCheck(draft.itemStatusChecks, 'wobbles'),
        usbItems: pickCheck(draft.itemStatusChecks, 'usbItems'),
        powerAdapter: pickCheck(draft.itemStatusChecks, 'powerAdapter'),
      },
      otherNotes: draft.otherNotes,
    };
    return laptop;
  }
  if (deviceType === 'desktop') {
    const desktop: DesktopInspection = {
      deviceType: 'desktop',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        allFeetPresent: pickCheck(draft.itemStatusChecks, 'allFeetPresent'),
        usbItems: pickCheck(draft.itemStatusChecks, 'usbItems'),
      },
      otherNotes: draft.otherNotes,
    };
    return desktop;
  }
  if (deviceType === 'phone') {
    const phone: PhoneInspection = {
      deviceType: 'phone',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        screenGlass: pickCheck(draft.damageChecks, 'screenGlass'),
        rearHousing: pickCheck(draft.damageChecks, 'rearHousing'),
        frameCorners: pickCheck(draft.damageChecks, 'frameCorners'),
        cameraLens: pickCheck(draft.damageChecks, 'cameraLens'),
        buttons: pickCheck(draft.itemStatusChecks, 'buttons'),
        chargePort: pickCheck(draft.itemStatusChecks, 'chargePort'),
        simTray: pickCheck(draft.itemStatusChecks, 'simTray'),
        accessories: pickCheck(draft.itemStatusChecks, 'accessories'),
      },
      otherNotes: draft.otherNotes,
    };
    return phone;
  }
  if (deviceType === 'tablet') {
    const tablet: TabletInspection = {
      deviceType: 'tablet',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        screenGlass: pickCheck(draft.damageChecks, 'screenGlass'),
        rearHousing: pickCheck(draft.damageChecks, 'rearHousing'),
        frameCorners: pickCheck(draft.damageChecks, 'frameCorners'),
        cameraLens: pickCheck(draft.damageChecks, 'cameraLens'),
        buttons: pickCheck(draft.itemStatusChecks, 'buttons'),
        chargePort: pickCheck(draft.itemStatusChecks, 'chargePort'),
        accessories: pickCheck(draft.itemStatusChecks, 'accessories'),
      },
      otherNotes: draft.otherNotes,
    };
    return tablet;
  }
  if (deviceType === 'hard_drive') {
    const hardDrive: HardDriveInspection = {
      deviceType: 'hard_drive',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        enclosure: pickCheck(draft.damageChecks, 'enclosure'),
        connector: pickCheck(draft.damageChecks, 'connector'),
        dropImpact: pickCheck(draft.damageChecks, 'dropImpact'),
        cablePresent: pickCheck(draft.itemStatusChecks, 'cablePresent'),
        caddyPresent: pickCheck(draft.itemStatusChecks, 'caddyPresent'),
      },
      otherNotes: draft.otherNotes,
    };
    return hardDrive;
  }
  if (deviceType === 'usb_flash_drive') {
    const usb: UsbFlashDriveInspection = {
      deviceType: 'usb_flash_drive',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        usbConnector: pickCheck(draft.damageChecks, 'usbConnector'),
        casing: pickCheck(draft.damageChecks, 'casing'),
        capPresent: pickCheck(draft.itemStatusChecks, 'capPresent'),
      },
      otherNotes: draft.otherNotes,
    };
    return usb;
  }
  if (deviceType === 'console') {
    const consoleInspection: ConsoleInspection = {
      deviceType: 'console',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        casing: pickCheck(draft.damageChecks, 'casing'),
        ports: pickCheck(draft.damageChecks, 'ports'),
        discDrive: pickCheck(draft.damageChecks, 'discDrive'),
        controllers: pickCheck(draft.itemStatusChecks, 'controllers'),
        powerCable: pickCheck(draft.itemStatusChecks, 'powerCable'),
        hdmiCable: pickCheck(draft.itemStatusChecks, 'hdmiCable'),
      },
      otherNotes: draft.otherNotes,
    };
    return consoleInspection;
  }
  if (deviceType === 'other') {
    const other: OtherDeviceInspection = {
      deviceType: 'other',
      manufacturer: draft.manufacturer,
      serialNumber: draft.serialNumber,
      serialNotLegible: draft.serialNotLegible,
      brandNewInBox: draft.brandNewInBox,
      checks: {
        casing: pickCheck(draft.damageChecks, 'casing'),
        screen: pickCheck(draft.damageChecks, 'screen'),
        ports: pickCheck(draft.damageChecks, 'ports'),
        powerAdapter: pickCheck(draft.itemStatusChecks, 'powerAdapter'),
        accessories: pickCheck(draft.itemStatusChecks, 'accessories'),
      },
      otherNotes: draft.otherNotes,
    };
    return other;
  }
  const aio: AllInOneInspection = {
    deviceType: 'all_in_one',
    manufacturer: draft.manufacturer,
    serialNumber: draft.serialNumber,
    serialNotLegible: draft.serialNotLegible,
    brandNewInBox: draft.brandNewInBox,
    checks: {
      allFeetPresent: pickCheck(draft.itemStatusChecks, 'allFeetPresent'),
      usbItems: pickCheck(draft.itemStatusChecks, 'usbItems'),
      screenDamage: pickCheck(draft.damageChecks, 'screenDamage'),
    },
    otherNotes: draft.otherNotes,
  };
  return aio;
}

// ---------------------------------------------------------------------------
// Identify-step validation
// ---------------------------------------------------------------------------

export interface IdentifyValidation {
  manufacturer?: string;
  serialNumber?: string;
}

/** True when this device may be completed without a serial number. */
export function isSerialOptional(schema: InspectionSchemaEntry, draft: InspectionDraftState): boolean {
  return draft.serialNotLegible || !!schema.serialOptional?.(draft.manufacturer);
}

export function validateIdentifyStep(
  schema: InspectionSchemaEntry,
  draft: InspectionDraftState,
): IdentifyValidation {
  const errors: IdentifyValidation = {};
  if (!draft.manufacturer || !draft.manufacturer.trim()) {
    errors.manufacturer = 'Manufacturer is required';
  }
  if (!isSerialOptional(schema, draft) && (!draft.serialNumber || !draft.serialNumber.trim())) {
    errors.serialNumber = 'Serial number is required';
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Completion — what "Mark inspection complete" needs
// ---------------------------------------------------------------------------

export type InspectionSectionStatus = 'done' | 'todo';

export interface InspectionProgress {
  /** Done once the serial label photo is taken or "No visible serial" chosen. */
  photos: InspectionSectionStatus;
  identify: InspectionSectionStatus;
  /** null when the section isn't shown: no damage rows for this type, or a sealed box. */
  damage: InspectionSectionStatus | null;
  itemStatus: InspectionSectionStatus | null;
  /** Everything still needed, worded as sm4rt-api's refusal words it. Empty = completable. */
  gaps: string[];
  /** The same, short enough for a phone-width action bar ("photo · serial · 3 damage"). */
  summary: string[];
}

function unanswered(rows: InspectionCheckRow[], checks: Record<string, CheckInInspectionCheck>): string[] {
  return rows
    .filter(row => {
      const status = checks[row.key]?.status;
      return status !== 'yes' && status !== 'no';
    })
    .map(row => row.label);
}

/**
 * Per-section Done / To do, and whether the inspection can be completed.
 *
 * **Mirrors `completion_gaps` in sm4rt-api's `check_in_inspection_rules.py`**,
 * which refuses the completion this disables the button for. Change the two
 * together: a drift is a button that 400s, or a finished inspection the page
 * still calls unfinished. The rule: the serial label photo (or "No visible
 * serial"), identity, and every damage / item-status row answered — a sealed
 * box skips the rows, not the serial photo. The other guided shots are
 * suggestions and never block.
 */
export function inspectionProgress(
  schema: InspectionSchemaEntry,
  draft: InspectionDraftState,
  hasSerialPhoto: boolean,
): InspectionProgress {
  const gaps: string[] = [];
  const serialResolved = hasSerialPhoto || draft.serialNotLegible;
  if (!serialResolved) gaps.push('the serial label photo, or No visible serial');

  const identityErrors = validateIdentifyStep(schema, draft);
  if (identityErrors.manufacturer) gaps.push('the manufacturer');
  if (identityErrors.serialNumber) gaps.push('the serial number');

  const physical = !draft.brandNewInBox;
  const missingDamage = physical ? unanswered(schema.damageRows, draft.damageChecks) : [];
  const missingStatus = physical ? unanswered(schema.itemStatusRows, draft.itemStatusChecks) : [];
  if (missingDamage.length) gaps.push(`Damage: ${missingDamage.join(', ')}`);
  if (missingStatus.length) gaps.push(`Item status: ${missingStatus.join(', ')}`);

  const summary: string[] = [];
  if (!serialResolved) summary.push('serial photo');
  if (identityErrors.manufacturer) summary.push('manufacturer');
  if (identityErrors.serialNumber) summary.push('serial');
  if (missingDamage.length) summary.push(`${missingDamage.length} damage`);
  if (missingStatus.length) summary.push(`${missingStatus.length} item`);

  const mark = (done: boolean): InspectionSectionStatus => (done ? 'done' : 'todo');
  return {
    photos: mark(serialResolved),
    identify: mark(Object.keys(identityErrors).length === 0),
    damage: physical && schema.damageRows.length > 0 ? mark(missingDamage.length === 0) : null,
    itemStatus: physical && schema.itemStatusRows.length > 0 ? mark(missingStatus.length === 0) : null,
    gaps,
    summary,
  };
}

// ---------------------------------------------------------------------------
// AI label reading (psar01) — pre-filling Identify from a photo
// ---------------------------------------------------------------------------

export interface PhotoIdentityReading {
  photoId: string;
  manufacturer: string | null;
  serialNumber: string | null;
  modelNumber: string | null;
  emcNumber: string | null;
}

/**
 * The best label reading among a device's photos: outcome `read`, highest
 * confidence, newest on a tie. Null while nothing has been read.
 */
export function bestPhotoReading(photos: CheckInPhoto[]): PhotoIdentityReading | null {
  let best: { photo: CheckInPhoto; confidence: number } | null = null;
  for (const photo of photos) {
    const reading = photo.aiReading;
    if (!reading || reading.outcome !== 'read') continue;
    if (!reading.manufacturer && !reading.serialNumber && !reading.modelNumber && !reading.emcNumber) continue;
    const confidence = reading.confidence ?? 0;
    if (
      !best ||
      confidence > best.confidence ||
      (confidence === best.confidence && photo.uploadedAt > best.photo.uploadedAt)
    ) {
      best = { photo, confidence };
    }
  }
  if (!best) return null;
  const reading = best.photo.aiReading!;
  return {
    photoId: best.photo.id,
    manufacturer: reading.manufacturer,
    serialNumber: reading.serialNumber,
    modelNumber: reading.modelNumber ?? null,
    emcNumber: reading.emcNumber ?? null,
  };
}

/**
 * Fit a manufacturer the model read ("Dell Inc.", "LENOVO") to this device
 * type's list: an exact match ignoring case, else the longest option the
 * reading contains. A fixed list (`dropdown`) takes nothing else — a value the
 * select can't show would be invisible to staff. Free text keeps the reading.
 */
export function matchManufacturer(schema: InspectionSchemaEntry, read: string | null): string | null {
  const text = (read ?? '').trim();
  if (!text) return null;
  const lower = text.toLowerCase();
  const exact = schema.manufacturerOptions.find(o => o.toLowerCase() === lower);
  if (exact) return exact;
  const contained = schema.manufacturerOptions
    .filter(o => o.length >= 2 && new RegExp(`\\b${escapeRegExp(o.toLowerCase())}\\b`).test(lower))
    .sort((a, b) => b.length - a.length)[0];
  if (contained) return contained;
  return schema.manufacturerKind === 'dropdown' ? null : text;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * The fields a reading should fill: only those still blank. Never overwrites
 * what staff typed (owner's decision, 2026-10-10).
 */
/** The Identify fields a label reading can fill. */
export type AiFillableField = 'manufacturer' | 'serialNumber' | 'modelNumber' | 'emcNumber';

export function aiFillPatch(
  schema: InspectionSchemaEntry,
  draft: InspectionDraftState,
  reading: PhotoIdentityReading,
): Partial<Pick<InspectionDraftState, AiFillableField | 'aiFilled'>> {
  const patch: Partial<Pick<InspectionDraftState, AiFillableField | 'aiFilled'>> = {};
  const aiFilled = { ...draft.aiFilled };
  const manufacturer = matchManufacturer(schema, reading.manufacturer);
  if (!draft.manufacturer?.trim() && manufacturer) {
    patch.manufacturer = manufacturer;
    aiFilled.manufacturer = reading.photoId;
  }
  if (!draft.serialNumber?.trim() && reading.serialNumber) {
    patch.serialNumber = reading.serialNumber;
    aiFilled.serialNumber = reading.photoId;
  }
  if (!draft.modelNumber?.trim() && reading.modelNumber) {
    patch.modelNumber = reading.modelNumber;
    aiFilled.modelNumber = reading.photoId;
  }
  // The EMC number only exists on an Apple computer; a reading naming one
  // elsewhere is the model guessing.
  if (!draft.emcNumber?.trim() && reading.emcNumber && showsEmcNumber(schema.deviceType, patch.manufacturer ?? draft.manufacturer)) {
    patch.emcNumber = reading.emcNumber;
    aiFilled.emcNumber = reading.photoId;
  }
  if (
    patch.manufacturer !== undefined ||
    patch.serialNumber !== undefined ||
    patch.modelNumber !== undefined ||
    patch.emcNumber !== undefined
  ) {
    patch.aiFilled = aiFilled;
  }
  return patch;
}

// ---------------------------------------------------------------------------
// Model number — the manufacturer's, off the device or its label
// ---------------------------------------------------------------------------

/**
 * Trim, collapse spaces, upper-case, and drop a leading "Model" word — Surface
 * labels print "Model 1866", and the number is what identifies it. Blank → null.
 */
export function normaliseModelNumber(value: string | null | undefined): string | null {
  const text = (value ?? '').replace(/\s+/g, ' ').trim().replace(/^model(?:\s+(?:no\.?|number))?(?:\s*[:#]\s*|\s+)/i, '');
  return text ? text.toUpperCase() : null;
}

/** "EMC 3598" → "3598": the label word is not part of the number. Blank → null. */
export function normaliseEmcNumber(value: string | null | undefined): string | null {
  const text = (value ?? '').replace(/\s+/g, ' ').trim().replace(/^emc\s*:?\s*/i, '');
  return text ? text.toUpperCase() : null;
}

const APPLE_COMPUTERS: ReadonlySet<CheckInDeviceType> = new Set(['laptop', 'desktop', 'all_in_one']);

/**
 * Only an Apple computer prints an EMC number (beside the A-number on the
 * case). It tells apart generations sharing an A-number; captured, not resolved.
 */
export function showsEmcNumber(deviceType: CheckInDeviceType, manufacturer: string | null | undefined): boolean {
  return APPLE_COMPUTERS.has(deviceType) && (manufacturer ?? '').trim().toLowerCase() === 'apple';
}

const MODEL_NUMBER_HINTS: Record<string, string> = {
  apple: 'The A-number, on the case or in Settings › General › About (e.g. A2338)',
  microsoft: 'The "Model" number under the kickstand or on the base (e.g. 1866)',
  lenovo: 'The machine type-model (MTM) on the label (e.g. 20XW0026UE)',
  hp: 'The product number (P/N) on the label (e.g. 6G8V1PA)',
  dell: 'The model on the label (e.g. P104F)',
  samsung: 'The model in Settings › About (e.g. SM-S918B)',
};

/** Where to find the model number for this manufacturer, for the field's helper text. */
export function modelNumberHint(
  manufacturer: string | null | undefined,
  deviceType?: CheckInDeviceType,
): string {
  const key = (manufacturer ?? '').trim().toLowerCase();
  if (key === 'apple' && deviceType && APPLE_COMPUTERS.has(deviceType)) {
    return 'The A-number in the small print on the base or back (e.g. A2681)';
  }
  return MODEL_NUMBER_HINTS[key] ?? 'The model number on the label, if there is one';
}
